/**
 * Extranet rule-context schemas + factories.
 *
 * {@link extranetBaseContextSchema} is the allow-list every extranet page
 * exposes through `RuleContextProvider` (tenant, VEOS environment, session);
 * a page that needs more variables extends it
 * (`{ ...extranetBaseContextSchema, contract_status: {…} }`) and its builder
 * spreads {@link buildExtranetBaseRuleContext}. Registrations pass the page's
 * schema as `contextVariables`, so the admin config page and the live
 * provider stay in lockstep.
 *
 * Static layout rules (rename / hide / reorder / tooltip) need no context at
 * all — the provider only matters for conditional rules.
 */

import { useMemo } from 'react';
import { useParams } from 'react-router-dom';

import type { ContextVariableDescriptor } from '@components/shared/configurable';

import { isSpoofedSession, useExtranetAuthStore } from './auth/store';
import type { ExtranetPerson } from './auth/types';

/** Variables shared by every extranet page. */
export const extranetBaseContextSchema = {
  tenant_slug: {
    type: 'string',
    description: 'Active tenant slug from the URL — enables per-tenant targeting.',
  },
  registry_slug: {
    type: 'string',
    description: 'Slug of the VEOS environment registry the user logged into.',
  },
  erp_environment: {
    type: 'string',
    description:
      'Environment of that registry (production / preprod / recette / …) — e.g. show a field only in recette.',
    sourceField: 'environment',
  },
  is_production: {
    type: 'boolean',
    description: 'True when the user is connected to the production VEOS environment.',
  },
  can_switch_session: {
    type: 'boolean',
    description: 'True for SUPERVUE users, who may switch to another session.',
    sourceField: 'SUPERVUE',
  },
  is_spoofed_session: {
    type: 'boolean',
    description:
      'True when a SUPERVUE user works on another session than their own (ID_PER_SESSION ≠ ID_PER).',
  },
} as const satisfies Record<string, ContextVariableDescriptor>;

/** Literal union of every key in {@link extranetBaseContextSchema}. */
export type ExtranetBaseContextKey = keyof typeof extranetBaseContextSchema;

/** Inputs of {@link buildExtranetBaseRuleContext}. */
export interface ExtranetBaseRuleContextArgs {
  tenantSlug: string | undefined;
  registrySlug: string | null;
  environment: string | null;
  isProduction: boolean;
  canSwitchSession: boolean;
  sessionId: number | null;
  person: ExtranetPerson | null;
}

/**
 * Build the base rule-context bag. Plain function (not a hook) so pages can
 * memoise it on primitive deps, or spread it into a page-specific bag.
 */
export function buildExtranetBaseRuleContext(
  args: ExtranetBaseRuleContextArgs,
): Record<ExtranetBaseContextKey, unknown> {
  return {
    tenant_slug: args.tenantSlug,
    registry_slug: args.registrySlug ?? undefined,
    erp_environment: args.environment ?? undefined,
    is_production: args.isProduction,
    can_switch_session: args.canSwitchSession,
    is_spoofed_session: isSpoofedSession(args.sessionId, args.person),
  };
}

/**
 * The base rule-context bag of the current page, memoised on its primitive
 * inputs (store + `:tenantSlug`).
 */
export function useExtranetBaseRuleContext(): Record<ExtranetBaseContextKey, unknown> {
  const { tenantSlug } = useParams<{ tenantSlug: string }>();
  const registrySlug = useExtranetAuthStore((s) => s.registrySlug);
  const environment = useExtranetAuthStore((s) => s.environment);
  const isProduction = useExtranetAuthStore((s) => s.isProduction);
  const canSwitchSession = useExtranetAuthStore((s) => s.canSwitchSession);
  const sessionId = useExtranetAuthStore((s) => s.sessionId);
  const personId = useExtranetAuthStore((s) => s.person?.id ?? null);

  return useMemo(
    () =>
      buildExtranetBaseRuleContext({
        tenantSlug,
        registrySlug,
        environment,
        isProduction,
        canSwitchSession,
        sessionId,
        person: personId === null ? null : { id: personId, nom: null, prenom: null },
      }),
    [tenantSlug, registrySlug, environment, isProduction, canSwitchSession, sessionId, personId],
  );
}
